import { useEffect, useMemo, useState } from 'react';
import type { Presentation } from '../../types';
import { DeepLinkCta } from './DeepLinkCta';

interface Props {
  p: Extract<Presentation, { type: 'table' }>;
}

const PAGE_SIZE = 25;

// Simple sortable + paginated table. Replaces the design-doc's Grid.js suggestion
// to keep the bundle small — Grid.js adds ~50KB for features we barely use here.
export function TablePresentation({ p }: Props) {
  const [sortIdx, setSortIdx] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);

  // When the underlying rows change (e.g., a follow-up question replaces the
  // composite), reset paging + sort so the user lands on row 1 of the new
  // dataset rather than an out-of-range empty page.
  useEffect(() => {
    setPage(0);
    setSortIdx(null);
    setSortDir('asc');
  }, [p.rows]);

  const sorted = useMemo(() => {
    if (sortIdx === null) return p.rows;
    const arr = [...p.rows];
    arr.sort((a, b) => {
      const av = a[sortIdx];
      const bv = b[sortIdx];
      if (av === bv) return 0;
      const cmp = av > bv ? 1 : -1;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [p.rows, sortIdx, sortDir]);

  const pageRows = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));

  return (
    <div className="presentation-card">
      <div className="presentation-title">⊞ Results ({p.rows.length} rows)</div>
      <div className="table-wrap">
        <table className="presentation-table">
          <thead>
            <tr>
              {p.columns.map((c, i) => (
                <th
                  key={i}
                  onClick={() => {
                    if (sortIdx === i) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                    else {
                      setSortIdx(i);
                      setSortDir('asc');
                    }
                  }}
                  className="sortable"
                >
                  {c} {sortIdx === i ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j}>{String(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="table-pagination">
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>
            ← Prev
          </button>
          <span>
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page === totalPages - 1}
          >
            Next →
          </button>
        </div>
      )}
      {p.cta && <DeepLinkCta cta={p.cta} />}
    </div>
  );
}
